import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { useClickSound } from '../hooks/useClickSound';
import { likeProject, getProjectLikes, getAchievements, getProjectById } from '../utils/api';
import type { Project, ProjectMember, Achievement } from '../types';

interface ProjectViewerProps {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (project: Project) => void;
  canEdit?: boolean;
}

export default function ProjectViewer({ project: initialProject, isOpen, onClose, onEdit, canEdit = false }: ProjectViewerProps) {
  const { withClickSound } = useClickSound();
  const { isAuthenticated } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState<'about' | 'paper' | 'media' | 'team'>('about');
  const [selectedMedia, setSelectedMedia] = useState<string | null>(null);
  const [featuredMediaIndex, setFeaturedMediaIndex] = useState(0);
  
  // Fetch full project data with members when opened
  const { data: projectData } = useQuery({
    queryKey: ['project', initialProject.id],
    queryFn: () => getProjectById(initialProject.id),
    enabled: isOpen,
    staleTime: 30000,
  });
  
  const project = projectData?.success && projectData.data?.project 
    ? projectData.data.project 
    : initialProject;
  
  const [likeCount, setLikeCount] = useState(initialProject.likeCount || 0);
  const [isLiked, setIsLiked] = useState(initialProject.isLiked || false);
  const [liking, setLiking] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Update like state when project changes
  useEffect(() => {
    if (project) {
      setLikeCount(project.likeCount || 0);
      setIsLiked(project.isLiked || false);
    }
  }, [project?.likeCount, project?.isLiked, project?.id]);

  // Fetch likes if not in project data
  const { data: likesData } = useQuery({
    queryKey: ['projectLikes', project?.id],
    queryFn: () => getProjectLikes(project!.id),
    enabled: isOpen && !!project && project.likeCount === undefined,
  });

  useEffect(() => {
    if (likesData?.success && likesData.data) {
      setLikeCount(likesData.data.likeCount);
      setIsLiked(likesData.data.isLiked || false);
    }
  }, [likesData]);

  // Fetch achievements for all project members
  const memberUserIds = project?.members?.map(m => m.user?.id).filter(Boolean) || [];
  const { data: achievementsData } = useQuery({
    queryKey: ['projectAchievements', project?.id, memberUserIds.join(',')],
    queryFn: async () => {
      const allAchievements: Achievement[] = [];
      for (const userId of memberUserIds) {
        if (userId) {
          try {
            const response = await getAchievements(userId);
            if (response.success && response.data?.achievements) {
              allAchievements.push(...response.data.achievements);
            }
          } catch (err) {
            console.error(`Failed to fetch achievements for user ${userId}:`, err);
          }
        }
      }
      return { achievements: allAchievements };
    },
    enabled: isOpen && !!project,
    staleTime: 30000, // Cache for 30 seconds
  });

  const allAchievements = achievementsData?.achievements || [];
  const grants = allAchievements.filter(a => a.type === 'grant_winner');

  const handleLike = async () => {
    if (!isAuthenticated || liking) return;
    
    const previousLikeCount = likeCount;
    const previousIsLiked = isLiked;
    
    // Optimistic update
    setLiking(true);
    setIsLiked(!isLiked);
    setLikeCount(prev => isLiked ? prev - 1 : prev + 1);

    try {
      const response = await likeProject(project!.id);
      if (response.success && response.data) {
        setLikeCount(response.data.likeCount);
        setIsLiked(response.data.isLiked);
      }
    } catch (err) {
      // Rollback on error
      setLikeCount(previousLikeCount);
      setIsLiked(previousIsLiked);
      console.error('Failed to like project:', err);
    } finally {
      setLiking(false);
    }
  };

  if (!isOpen || !project) return null;

  const bannerImage = project.banner?.thumbnailUrl || project.banner?.url || project.media?.find(m => m.mediaType === 'image')?.url;
  const projectLogo = project.logo?.thumbnailUrl || project.logo?.url || project.team?.logoUrl || project.media?.find(m => m.mediaType === 'image')?.url;
  const images = project.media?.filter(m => m.mediaType === 'image').map(m => ({ id: m.id, url: m.url, mediaType: m.mediaType as 'image' | 'video' })) || [];
  const videos = project.media?.filter(m => m.mediaType === 'video').map(m => ({ id: m.id, url: m.url, mediaType: m.mediaType as 'image' | 'video' })) || [];

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.85)',
    backdropFilter: 'blur(12px)',
    zIndex: isMobile ? 300 : 60, // Higher on mobile to be above bottom nav
  };

  const panelStyle: CSSProperties = {
    position: 'fixed',
    zIndex: isMobile ? 301 : 70, // Higher on mobile to be above bottom nav
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#0e1621',
    ...(isMobile ? {
      bottom: 0,
      left: 0,
      right: 0,
      top: 0,
      borderRadius: 0,
    } : {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 'min(1400px, 95vw)',
      height: 'min(90vh, 900px)',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    }),
  };

  return (
    <>
      <div style={backdropStyle} onClick={onClose} />

      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        {/* Hero Banner */}
        <div style={{
          position: 'relative',
          width: '100%',
          height: isMobile ? '200px' : '300px',
          background: bannerImage 
            ? `linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%), url(${bannerImage})`
            : 'linear-gradient(135deg, #9945FF 0%, #14F195 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          flexShrink: 0,
        }}>
          {/* Overlay gradient */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(14, 22, 33, 0) 0%, rgba(14, 22, 33, 0.8) 100%)',
          }} />

          {/* Top controls */}
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            display: 'flex',
            gap: '8px',
            zIndex: 10,
          }}>
            {/* Like Button */}
            {isAuthenticated && (
              <button
                onClick={withClickSound(handleLike)}
                disabled={liking}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  background: isLiked 
                    ? 'rgba(20, 241, 149, 0.2)' 
                    : 'rgba(255, 255, 255, 0.1)',
                  border: `1px solid ${isLiked ? '#14F195' : 'rgba(255, 255, 255, 0.2)'}`,
                  color: isLiked ? '#14F195' : 'rgba(255, 255, 255, 0.8)',
                  cursor: liking ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!liking) {
                    e.currentTarget.style.background = isLiked 
                      ? 'rgba(20, 241, 149, 0.3)' 
                      : 'rgba(255, 255, 255, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isLiked 
                    ? 'rgba(20, 241, 149, 0.2)' 
                    : 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <svg 
                  style={{ width: '18px', height: '18px', fill: isLiked ? '#14F195' : 'none', stroke: 'currentColor', strokeWidth: 2 }}
                  viewBox="0 0 24 24"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                {likeCount > 0 && <span>{likeCount}</span>}
              </button>
            )}
            
            {/* Like count for non-authenticated */}
            {!isAuthenticated && likeCount > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                fontWeight: 600,
              }}>
                <svg style={{ width: '18px', height: '18px', fill: 'none', stroke: 'currentColor', strokeWidth: 2 }} viewBox="0 0 24 24">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                {likeCount}
              </div>
            )}

            {canEdit && onEdit && (
              <button
                onClick={withClickSound(() => onEdit(project))}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Edit
              </button>
            )}

            <button
              onClick={withClickSound(onClose)}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: 'rgba(0,0,0,0.5)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ×
            </button>
          </div>

          {/* Project info overlay */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '24px',
            display: 'flex',
            alignItems: 'flex-end',
            gap: '20px',
          }}>
            {/* Project Logo */}
            {projectLogo ? (
              <img
                src={projectLogo}
                alt={project.name}
                onError={(e) => {
                  // Fallback to default logo if image fails to load
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent && !parent.querySelector('.logo-fallback')) {
                    const fallback = document.createElement('div');
                    fallback.className = 'logo-fallback';
                    fallback.style.cssText = `
                      width: ${isMobile ? '80px' : '120px'};
                      height: ${isMobile ? '80px' : '120px'};
                      border-radius: 12px;
                      background: linear-gradient(135deg, #9945FF, #14F195);
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      font-size: ${isMobile ? '32px' : '48px'};
                      border: 3px solid rgba(255, 255, 255, 0.1);
                      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
                      flex-shrink: 0;
                    `;
                    fallback.textContent = '🚀';
                    parent.appendChild(fallback);
                  }
                }}
                style={{
                  width: isMobile ? '80px' : '120px',
                  height: isMobile ? '80px' : '120px',
                  borderRadius: '12px',
                  objectFit: 'cover',
                  border: '3px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                  flexShrink: 0,
                }}
              />
            ) : (
              <div style={{
                width: isMobile ? '80px' : '120px',
                height: isMobile ? '80px' : '120px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #9945FF, #14F195)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '32px' : '48px',
                border: '3px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                flexShrink: 0,
              }}>
                🚀
              </div>
            )}

            {/* Title and tagline */}
            <div style={{ flex: 1, minWidth: 0, paddingBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <h1 style={{ 
                  fontSize: isMobile ? '24px' : '32px', 
                  fontWeight: 700, 
                  color: 'white',
                  margin: 0,
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
                }}>
                  {project.name}
                </h1>
                {project.xVerifiedAt && (
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: '#1d9bf0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <svg style={{ width: '14px', height: '14px', color: 'white' }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                  </div>
                )}
              </div>
              {project.tagline && (
                <p style={{ 
                  fontSize: isMobile ? '14px' : '16px', 
                  color: 'rgba(255,255,255,0.9)',
                  margin: 0,
                  textShadow: '0 1px 4px rgba(0, 0, 0, 0.5)',
                }}>
                  {project.tagline}
                </p>
              )}
              {project.team && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  {project.team.logoUrl && (
                    <img
                      src={project.team.logoUrl}
                      alt={project.team.name}
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                  <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    by {project.team.name}
                    {project.team.xVerifiedAt && (
                      <svg style={{ width: '14px', height: '14px', flexShrink: 0 }} viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="12" fill="#1DA1F2"/>
                        <path d="M9.5 12l1.5 1.5 3.5-3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          overflow: 'hidden',
          flexDirection: isMobile ? 'column' : 'row',
        }}>
          {/* Left Column (70%) */}
          <div style={{ 
            flex: isMobile ? '1' : '0 0 70%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRight: isMobile ? 'none' : '1px solid rgba(255, 255, 255, 0.05)',
          }}>
            {/* Tabs */}
            <div style={{ 
              flexShrink: 0,
              display: 'flex', 
              gap: '4px', 
              background: 'rgba(255,255,255,0.02)', 
              padding: '4px',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}>
              <button 
                onClick={withClickSound(() => setActiveTab('about'))} 
                style={tabStyle(activeTab === 'about')}
              >
                About
              </button>
              {images.length > 0 && (
                <button 
                  onClick={withClickSound(() => setActiveTab('media'))} 
                  style={tabStyle(activeTab === 'media')}
                >
                  Media ({images.length + videos.length})
                </button>
              )}
              <button 
                onClick={withClickSound(() => setActiveTab('team'))} 
                style={tabStyle(activeTab === 'team')}
              >
                Team
              </button>
            </div>

            {/* Tab Content */}
            <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px' : '24px', paddingBottom: isMobile ? '96px' : '24px' }}>
              {activeTab === 'about' && (
                <AboutTab project={project} />
              )}

              {activeTab === 'media' && (
                <MediaTabSteam
                  images={images}
                  videos={videos}
                  featuredIndex={featuredMediaIndex}
                  onSelectFeatured={setFeaturedMediaIndex}
                  onViewFullscreen={setSelectedMedia}
                />
              )}

              {activeTab === 'team' && (
                <TeamTab 
                  projectMembers={project.members || []}
                  project={project}
                />
              )}
            </div>
          </div>

          {/* Right Sidebar (30%) */}
          {!isMobile && (
            <div style={{ 
              flex: '0 0 30%',
              overflowY: 'auto',
              background: 'rgba(255,255,255,0.01)',
              padding: '24px',
            }}>
              <Sidebar 
                project={project}
                achievements={allAchievements}
                grants={grants}
              />
            </div>
          )}

          {/* Mobile Sidebar - Show at bottom */}
          {isMobile && (
            <div style={{ 
              flexShrink: 0,
              overflowY: 'auto',
              background: 'rgba(255,255,255,0.02)',
              padding: '16px',
              borderTop: '1px solid rgba(255,255,255,0.05)',
              maxHeight: '40vh',
            }}>
              <Sidebar 
                project={project}
                achievements={allAchievements}
                grants={grants}
              />
            </div>
          )}
        </div>
      </div>

      {/* Full Screen Media Viewer */}
      {selectedMedia && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.95)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
          }}
          onClick={() => setSelectedMedia(null)}
        >
          <img
            src={selectedMedia}
            alt=""
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
          />
        </div>
      )}
    </>
  );
}

const tabStyle = (isActive: boolean): CSSProperties => ({
  padding: '10px 16px',
  fontSize: '13px',
  fontWeight: 600,
  color: isActive ? 'white' : 'rgba(255,255,255,0.5)',
  background: isActive ? 'rgba(255,255,255,0.08)' : 'transparent',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.2s',
});

// Sidebar Component
function Sidebar({ 
  project, 
  achievements,
  grants,
}: { 
  project: Project;
  achievements: Achievement[];
  grants: Achievement[];
}) {
  const links = [
    { label: 'Website', url: project.website, icon: '🌐' },
    { label: 'GitHub', url: project.github, icon: '💻' },
    { label: 'X (Twitter)', url: project.twitter?.startsWith('http') ? project.twitter : project.twitter ? `https://x.com/${project.twitter.replace('@', '')}` : null, icon: '𝕏' },
    { label: 'Discord', url: project.discord, icon: '💬' },
  ].filter(l => l.url);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Social Links */}
      {links.length > 0 && (
        <div style={{
          padding: '20px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.08)',
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'white', marginBottom: '12px' }}>
            Links
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {links.map((link, i) => (
              <a
                key={i}
                href={link.url!}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  color: 'rgba(255,255,255,0.8)',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
                }}
              >
                <span style={{ fontSize: '18px' }}>{link.icon}</span>
                <span style={{ fontSize: '14px', fontWeight: 500 }}>{link.label}</span>
                <div style={{ flex: 1 }} />
                <svg style={{ width: '16px', height: '16px', color: 'rgba(255,255,255,0.3)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 17L17 7M17 7H7M17 7V17"/>
                </svg>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Grants Section - Always show, even if empty */}
      <div style={{
        padding: '20px',
        background: grants.length > 0 
          ? 'linear-gradient(135deg, rgba(153, 69, 255, 0.1), rgba(20, 241, 149, 0.1))' 
          : 'rgba(255,255,255,0.03)',
        borderRadius: '12px',
        border: grants.length > 0 
          ? '1px solid rgba(153, 69, 255, 0.3)' 
          : '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{ marginBottom: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'white', margin: 0 }}>
            Grants Won {grants.length > 0 && `(${grants.length})`}
          </h3>
        </div>
        {grants.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {grants.map(grant => (
              <div
                key={grant.id}
                style={{
                  padding: '12px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'white', marginBottom: '4px' }}>
                  {grant.title}
                </div>
                {grant.issuer && (
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>
                    {grant.issuer}
                  </div>
                )}
                {grant.date && (
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                    {new Date(grant.date).toLocaleDateString()}
                  </div>
                )}
                {grant.proofUrl && (
                  <a
                    href={grant.proofUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '11px',
                      color: '#14F195',
                      textDecoration: 'none',
                      marginTop: '4px',
                      display: 'inline-block',
                    }}
                  >
                    View Proof →
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '12px' }}>
            No grants yet
          </div>
        )}
      </div>

      {/* Achievements Section - Always show */}
      <div style={{
        padding: '20px',
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.08)',
      }}>
        <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'white', marginBottom: '12px' }}>
          Achievements {achievements.length > 0 && `(${achievements.length})`}
        </h3>
        {achievements.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {achievements.slice(0, 5).map(achievement => (
              <div
                key={achievement.id}
                style={{
                  padding: '10px',
                  background: 'rgba(255,255,255,0.02)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                <div style={{ fontSize: '13px', fontWeight: 500, color: 'white', marginBottom: '4px' }}>
                  {achievement.title}
                </div>
                {achievement.issuer && (
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                    {achievement.issuer}
                  </div>
                )}
              </div>
            ))}
            {achievements.length > 5 && (
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '8px' }}>
                +{achievements.length - 5} more
              </div>
            )}
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '12px' }}>
            No achievements yet
          </div>
        )}
      </div>

      {/* Project Stats */}
      <div style={{
        padding: '20px',
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.08)',
      }}>
        <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'white', marginBottom: '12px' }}>
          Project Info
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.7)' }}>
            <span>Status</span>
            <span style={{ 
              color: project.status === 'published' ? '#14F195' : '#ffc107',
              fontWeight: 600,
            }}>
              {project.status === 'published' ? 'Published' : 'Draft'}
            </span>
          </div>
          {project.members && project.members.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.7)' }}>
              <span>Team Members</span>
              <span style={{ color: 'white', fontWeight: 600 }}>{project.members.length}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.7)' }}>
            <span>Created</span>
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>
              {new Date(project.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      {/* Team Card */}
      {project.team && (
        <div style={{
          padding: '20px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.08)',
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'white', marginBottom: '12px' }}>
            Team
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {project.team.logoUrl ? (
              <img
                src={project.team.logoUrl}
                alt={project.team.name}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #9945FF, #14F195)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
              }}>
                {project.team.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {project.team.name}
                {project.team.xVerifiedAt && (
                  <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="12" fill="#1DA1F2"/>
                    <path d="M9.5 12l1.5 1.5 3.5-3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              {project.team.memberCount > 0 && (
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                  {project.team.memberCount} member{project.team.memberCount !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
          
          {/* Owner/Creator */}
          {project.team.owner && (
            <div style={{ 
              marginTop: '16px', 
              paddingTop: '16px', 
              borderTop: '1px solid rgba(255,255,255,0.08)' 
            }}>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>
                Created by
              </div>
              <a
                href={`https://x.com/${project.team.owner.xUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                <img
                  src={project.team.owner.xAvatarUrl || '/placeholder-avatar.png'}
                  alt={project.team.owner.xDisplayName}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    objectFit: 'cover',
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>
                    {project.team.owner.xDisplayName}
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                    @{project.team.owner.xUsername}
                  </div>
                </div>
                <svg style={{ width: '14px', height: '14px', color: 'rgba(255,255,255,0.3)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 17L17 7M17 7H7M17 7V17"/>
                </svg>
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// About Tab (includes description and paper content)
function AboutTab({ project }: { project: Project }) {
  const renderMarkdown = (text: string) => {
    return text
      .split('\n')
      .map((line, i) => {
        if (line.startsWith('### ')) {
          return <h4 key={i} style={{ fontSize: '16px', fontWeight: 600, color: 'white', marginTop: '20px', marginBottom: '10px' }}>{line.slice(4)}</h4>;
        }
        if (line.startsWith('## ')) {
          return <h3 key={i} style={{ fontSize: '18px', fontWeight: 600, color: 'white', marginTop: '24px', marginBottom: '12px' }}>{line.slice(3)}</h3>;
        }
        if (line.startsWith('# ')) {
          return <h2 key={i} style={{ fontSize: '22px', fontWeight: 700, color: 'white', marginTop: '28px', marginBottom: '14px' }}>{line.slice(2)}</h2>;
        }
        if (line.trim() === '') {
          return <br key={i} />;
        }
        return <p key={i} style={{ fontSize: '15px', lineHeight: 1.8, color: 'rgba(255,255,255,0.8)', marginBottom: '12px' }}>{line}</p>;
      });
  };

  return (
    <div>
      {/* Description */}
      {project.description ? (
        <div style={{ marginBottom: project.paperContent ? '32px' : '0' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'white', marginBottom: '16px' }}>
            Description
          </h3>
          <div style={{
            fontSize: '15px',
            lineHeight: 1.8,
            color: 'rgba(255,255,255,0.8)',
            whiteSpace: 'pre-wrap',
          }}>
            {project.description}
          </div>
        </div>
      ) : (
        !project.paperContent && (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            background: 'rgba(255,255,255,0.02)',
            borderRadius: '12px',
          }}>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
              No description provided
            </p>
          </div>
        )
      )}

      {/* Paper Content */}
      {project.paperContent && (
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'white', marginBottom: '16px' }}>
            Technical Paper / Documentation
          </h3>
          <div style={{ fontFamily: 'inherit' }}>
            {renderMarkdown(project.paperContent)}
          </div>
        </div>
      )}
    </div>
  );
}


// Steam-style Media Tab
function MediaTabSteam({
  images,
  videos,
  featuredIndex,
  onSelectFeatured,
  onViewFullscreen,
}: {
  images: { id: string; url: string; mediaType: 'image' | 'video' }[];
  videos: { id: string; url: string; mediaType: 'image' | 'video' }[];
  featuredIndex: number;
  onSelectFeatured: (index: number) => void;
  onViewFullscreen: (url: string | null) => void;
}) {
  const allMedia = [...images, ...videos];
  const featured = allMedia[featuredIndex];

  if (allMedia.length === 0) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        background: 'rgba(255,255,255,0.02)',
        borderRadius: '12px',
      }}>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
          No media available
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Featured Media */}
      {featured && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16/9',
            borderRadius: '12px',
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.05)',
            cursor: featured.mediaType === 'image' ? 'zoom-in' : 'default',
          }}
          onClick={() => {
            if (featured.mediaType === 'image') {
              onViewFullscreen(featured.url);
            }
          }}
        >
          {featured.mediaType === 'image' ? (
            <img
              src={featured.url}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <video
              src={featured.url}
              controls
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}
        </div>
      )}

      {/* Thumbnail Strip */}
      {allMedia.length > 1 && (
        <div style={{
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          paddingBottom: '8px',
        }}>
          {allMedia.map((item, index) => (
            <div
              key={item.id}
              onClick={() => onSelectFeatured(index)}
              style={{
                flexShrink: 0,
                width: '120px',
                height: '68px',
                borderRadius: '8px',
                overflow: 'hidden',
                cursor: 'pointer',
                border: featuredIndex === index 
                  ? '2px solid #14F195' 
                  : '2px solid transparent',
                background: 'rgba(255,255,255,0.05)',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = featuredIndex === index 
                  ? '#14F195' 
                  : 'rgba(255,255,255,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = featuredIndex === index 
                  ? '#14F195' 
                  : 'transparent';
              }}
            >
              {item.mediaType === 'image' ? (
                <img
                  src={item.url}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.3)',
                }}>
                  <svg style={{ width: '24px', height: '24px', color: 'white' }} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Team Tab
function TeamTab({ projectMembers, project }: { projectMembers: ProjectMember[]; project: Project }) {
  const projectRoleColors: Record<string, string> = {
    lead: '#9945FF',
    developer: '#14F195',
    designer: '#FF6B9D',
    marketer: '#FFC107',
    researcher: '#2196F3',
    contributor: 'rgba(255,255,255,0.5)',
    advisor: '#9C27B0',
  };

  if (projectMembers.length === 0) {
    // Show team owner/creator if no members assigned
    if (project.team?.owner) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'white', marginBottom: '16px' }}>
              Project Creator
            </h3>
            <a
              href={`https://x.com/${project.team.owner.xUsername}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '20px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '12px',
                textDecoration: 'none',
                transition: 'all 0.2s',
                maxWidth: '400px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <img
                src={project.team.owner.xAvatarUrl || '/placeholder-avatar.png'}
                alt={project.team.owner.xDisplayName}
                style={{ width: '64px', height: '64px', borderRadius: '12px', objectFit: 'cover' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: 600, color: 'white', marginBottom: '4px' }}>
                  {project.team.owner.xDisplayName}
                </div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>
                  @{project.team.owner.xUsername}
                </div>
                <span style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '4px 8px',
                  borderRadius: '6px',
                  background: 'rgba(153, 69, 255, 0.2)',
                  color: '#9945FF',
                  textTransform: 'uppercase',
                }}>
                  Creator
                </span>
              </div>
              <svg style={{ width: '18px', height: '18px', color: 'rgba(255,255,255,0.3)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 17L17 7M17 7H7M17 7V17"/>
              </svg>
            </a>
          </div>
          <div style={{
            padding: '20px',
            textAlign: 'center',
            background: 'rgba(255,255,255,0.02)',
            borderRadius: '12px',
          }}>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
              No additional members assigned to this project
            </p>
          </div>
        </div>
      );
    }
    
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        background: 'rgba(255,255,255,0.02)',
        borderRadius: '12px',
      }}>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
          No members assigned to this project
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
      {projectMembers.map(projectMember => {
        if (!projectMember.user) return null;
        
        return (
          <a
            key={projectMember.id}
            href={`https://x.com/${projectMember.user.xUsername}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '12px',
              textDecoration: 'none',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <img
              src={projectMember.user.xAvatarUrl || '/placeholder-avatar.png'}
              alt={projectMember.user.xDisplayName}
              style={{ width: '64px', height: '64px', borderRadius: '12px', objectFit: 'cover' }}
            />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'white', marginBottom: '4px' }}>
                {projectMember.user.xDisplayName}
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>
                @{projectMember.user.xUsername}
              </div>
              <span style={{
                fontSize: '10px',
                fontWeight: 600,
                padding: '4px 8px',
                borderRadius: '6px',
                background: `${projectRoleColors[projectMember.role] || projectRoleColors.contributor}20`,
                color: projectRoleColors[projectMember.role] || projectRoleColors.contributor,
                textTransform: 'uppercase',
              }}>
                {projectMember.role}
              </span>
            </div>
          </a>
        );
      })}
    </div>
  );
}
